/**
 * `ExploreDetail` — dettaglio di un'esplorazione con i risultati di matching.
 *
 * Rotta `/explore/:id` (sezione "Explore", accessibile a ogni utente
 * autenticato). Mostra:
 *  - i criteri dell'esplorazione (skill cercate con il Livello richiesto);
 *  - i risultati (`GET /api/explorations/:id/results`): persone (utenti reali) e
 *    candidati esterni ordinati per qualità del match, con match%, copertura e
 *    skill soddisfatte.
 *
 * L'autore (o un admin) può modificare le skill/nome (`PUT`) ed eliminare
 * l'esplorazione (`DELETE`). L'autorizzazione autorevole resta l'API: il
 * pulsante è nascosto quando il richiedente non è l'autore, ma l'API nega
 * comunque con `403`.
 */
import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ExploreMatchDto,
  ExploreResultsDto,
  Livello,
  SkillDto,
  UpdateExplorationRequest,
} from '../shared/dto';
import { LIVELLO_LABELS } from '../shared/dto';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import { ExplorationSkillEditor } from '../components/ExplorationSkillEditor';
import { scoreColor } from '../components/StarRating';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  Loading,
  Modal,
  PageHeader,
  TextInput,
} from '../components/ui';

const skillsKey = ['skills', 'catalog'] as const;

/** Chiave di cache dei risultati di una singola esplorazione. */
function resultsKey(id: string) {
  return ['explorations', id, 'results'] as const;
}

/** Formatta una percentuale `0..100` come intero con `%`. */
function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

/** Link al profilo del soggetto in base al tipo (persona → Insights, candidato → CV). */
function matchHref(match: ExploreMatchDto): string {
  return match.kind === 'person'
    ? `/insights/team/${match.id}`
    : `/external-cv/${match.id}`;
}

/** Raggio del cerchio del donut (viewBox 36×36, stroke 4 → centro a 18). */
const DONUT_RADIUS = 16;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * `ScoreDonut` — anello di progresso per una percentuale `0..100`. Replica il
 * donut della {@link CompetencyMatchCard} (stesse classi CSS
 * `competency-match-card__donut*`), così le card dell'Explore sono identiche a
 * quelle della pagina "Il mio CV".
 */
function ScoreDonut({ score, label }: { score: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, score));
  const rounded = Math.round(clamped);
  const ringColor = scoreColor(clamped / 10) ?? 'var(--color-primary)';
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE;

  return (
    <div
      className="competency-match-card__donut"
      role="img"
      aria-label={`${label} ${rounded} su 100`}
      title={`${label} ${rounded}/100`}
    >
      <svg
        viewBox="0 0 36 36"
        className="competency-match-card__donut-svg"
        aria-hidden="true"
      >
        <circle
          className="competency-match-card__donut-track"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
        />
        <circle
          className="competency-match-card__donut-fill"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
          stroke={ringColor}
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="competency-match-card__donut-value" aria-hidden="true">
        {rounded}
      </span>
    </div>
  );
}

/**
 * `MetricBar` — barra di progresso etichettata per una percentuale `0..100`,
 * con le stesse classi CSS della {@link CompetencyMatchCard}.
 */
function MetricBar({ label, value }: { label: string; value: number }) {
  const clamped = Math.min(100, Math.max(0, value));
  const rounded = Math.round(clamped);
  const color = scoreColor(clamped / 10);
  return (
    <div className="competency-match-card__metric">
      <dt>{label}</dt>
      <dd>
        <span
          role="progressbar"
          aria-valuenow={rounded}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} ${rounded}%`}
          className="competency-match-card__bar"
        >
          <span
            className="competency-match-card__bar-fill"
            style={{ width: `${clamped}%`, ...(color ? { background: color } : {}) }}
          />
        </span>
        <span className="competency-match-card__value">{formatPercent(clamped)}</span>
      </dd>
    </div>
  );
}

/**
 * `ExploreMatchCard` — card di un soggetto (persona o candidato) nei risultati
 * dell'esplorazione. Riusa integralmente lo stile della {@link CompetencyMatchCard}
 * (classi `competency-match-card__*`): donut della Copertura in alto a destra,
 * nome come titolo `<h3>` (la card intera è navigabile), barra della
 * percentuale di Match, e footer con il conteggio "N/M skill soddisfatte" e il
 * badge del tipo di soggetto.
 */
function ExploreMatchCard({ match, backTo }: { match: ExploreMatchDto; backTo: string }) {
  const navigate = useNavigate();

  // La card è navigabile come le card competenza: click o Invio/Spazio aprono
  // il profilo del soggetto. Il titolo resta un `<h3>` (stesso stile delle
  // altre card), non un link. Propaghiamo la provenienza (`state.from`) così la
  // pagina di dettaglio può tornare a questa esplorazione (pattern `JobMatchCard`).
  const openDetail = () => {
    void navigate(matchHref(match), { state: { from: backTo } });
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };

  return (
    <article
      className={[
        'competency-match-card',
        'competency-match-card--interactive',
        'competency-match-card--explore',
        // Le card del Team hanno una riga metrica in più (la Propensione): un
        // modificatore dedicato le alza di quella riga, così la Propensione del
        // team si allinea alla Copertura (ultima riga) delle card candidato.
        match.kind === 'person' ? 'competency-match-card--explore-person' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={handleKeyDown}
      aria-label={`Apri il profilo di ${match.displayName}`}
    >
      {/* Donut del Match: la metrica principale dell'esplorazione, in evidenza
          nell'angolo (come la Copertura nelle card competenza). */}
      <ScoreDonut score={match.matchPercent} label="Match" />

      <div className="competency-match-card__header">
        <h3 className="competency-match-card__name">{match.displayName}</h3>
      </div>

      <dl className="competency-match-card__metrics">
        <MetricBar label="Copertura" value={match.coveragePercent} />
        {/* Propensione (volontà di approfondire): mostrata solo quando
            disponibile — le persone con propensione espressa, mai i candidati.
            Nella sezione Team, se assente, si riserva comunque lo spazio con un
            segnaposto invisibile così le card restano della stessa altezza. */}
        {match.propensionPercent !== null && (
          <MetricBar label="Propensione" value={match.propensionPercent} />
        )}
      </dl>

      <div className="competency-match-card__footer">
        <span
          className="competency-match-card__skills"
          aria-label={`${match.metCount} di ${match.totalSkillCount} skill al livello richiesto`}
        >
          {match.metCount}/{match.totalSkillCount} skill al livello richiesto
        </span>
      </div>
    </article>
  );
}

/**
 * `MatchSection` — una delle due sezioni di risultati (Persone / Candidati),
 * con titolo e conteggio. Se non ci sono soggetti mostra un messaggio di vuoto
 * dedicato, così l'utente capisce che la sezione esiste ma è priva di risultati.
 */
function MatchSection({
  title,
  matches,
  backTo,
  emptyMessage,
}: {
  title: string;
  matches: ExploreMatchDto[];
  backTo: string;
  emptyMessage: string;
}) {
  return (
    <section className="explore-results-section" aria-label={title}>
      <h2 className="explore-results-section__title">
        {title}
        <span className="explore-results-section__count">{matches.length}</span>
      </h2>
      {matches.length === 0 ? (
        <EmptyState role="status">{emptyMessage}</EmptyState>
      ) : (
        <ul className="dashboard__matches" aria-label={`Risultati: ${title}`}>
          {matches.map((m) => (
            <li key={`${m.kind}:${m.id}`}>
              <ExploreMatchCard match={m} backTo={backTo} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function ExploreDetail() {
  const { id = '' } = useParams();
  const api = useApiClient();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { me } = useAuth();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [requiredLevels, setRequiredLevels] = useState<Record<string, Livello>>(
    {},
  );
  const [formError, setFormError] = useState<string | null>(null);

  const resultsQuery = useQuery({
    queryKey: resultsKey(id),
    queryFn: () => api.get<ExploreResultsDto>(`/api/explorations/${id}/results`),
    enabled: id !== '',
  });

  const skillsQuery = useQuery({
    queryKey: skillsKey,
    queryFn: () => api.get<SkillDto[]>('/api/skills'),
    enabled: isEditOpen,
  });

  const skillOptions = useMemo(
    () =>
      (skillsQuery.data ?? [])
        .map((s) => ({ id: s.id, name: s.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })),
    [skillsQuery.data],
  );

  const exploration = resultsQuery.data?.exploration ?? null;
  const matches = useMemo(
    () => resultsQuery.data?.matches ?? [],
    [resultsQuery.data],
  );
  // Partizione per tipo di soggetto (l'ordine per qualità del match è già
  // garantito dall'API e preservato dal filtro).
  const people = useMemo(
    () => matches.filter((m) => m.kind === 'person'),
    [matches],
  );
  const candidates = useMemo(
    () => matches.filter((m) => m.kind === 'candidate'),
    [matches],
  );

  // L'autore (o un admin) può modificare/eliminare.
  const canMutate =
    exploration !== null &&
    me !== null &&
    (exploration.createdBy === me.id || me.role === 'admin');

  // Precarica il form di modifica quando si apre il modale.
  useEffect(() => {
    if (isEditOpen && exploration !== null) {
      setName(exploration.name);
      setSelectedIds(exploration.skills.map((s) => s.skillId));
      setRequiredLevels(
        Object.fromEntries(
          exploration.skills.map((s) => [s.skillId, s.requiredLevel]),
        ),
      );
      setFormError(null);
    }
  }, [isEditOpen, exploration]);

  const updateExploration = useMutation({
    mutationFn: (body: UpdateExplorationRequest) =>
      api.put(`/api/explorations/${id}`, body),
    onSuccess: () => {
      setIsEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: resultsKey(id) });
      void queryClient.invalidateQueries({ queryKey: ['explorations'] });
    },
    onError: () => {
      setFormError('Salvataggio non riuscito. Riprova.');
    },
  });

  const deleteExploration = useMutation({
    mutationFn: () => api.delete(`/api/explorations/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['explorations'] });
      navigate('/explore');
    },
  });

  const trimmedName = name.trim();
  const allLevelsChosen = selectedIds.every(
    (sid) => requiredLevels[sid] !== undefined,
  );
  const canSubmit =
    trimmedName.length > 0 && selectedIds.length > 0 && allLevelsChosen;

  if (id === '') {
    return <Alert tone="error">Esplorazione non valida.</Alert>;
  }

  return (
    <section className="external-cv-list" aria-labelledby="explore-detail-title">
      {resultsQuery.isLoading ? (
        <Loading>Caricamento risultati…</Loading>
      ) : resultsQuery.isError || exploration === null ? (
        <Alert tone="error">Impossibile caricare l'esplorazione.</Alert>
      ) : (
        <>
          <PageHeader
            title={exploration.name}
            titleId="explore-detail-title"
            subtitle="Persone e candidati ordinati per qualità del match sulle skill cercate."
            actions={
              canMutate ? (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setIsEditOpen(true)}
                  >
                    Modifica
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    loading={deleteExploration.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          'Eliminare questa esplorazione? L\'azione non è reversibile.',
                        )
                      ) {
                        deleteExploration.mutate();
                      }
                    }}
                  >
                    Elimina
                  </Button>
                </>
              ) : null
            }
          />

          {/* Criteri di ricerca: skill cercate con il Livello richiesto. */}
          {exploration.skills.length === 0 ? (
            <Alert tone="info">
              Questa esplorazione non cerca ancora alcuna skill. Modificala per
              aggiungerne.
            </Alert>
          ) : (
            <div className="explore-criteria">
              <span className="explore-criteria__label">Skill cercate</span>
              <ul className="explore-criteria__list" aria-label="Skill cercate">
                {exploration.skills.map((s) => {
                  const match = matches[0]?.skills.find(
                    (ms) => ms.skillId === s.skillId,
                  );
                  const skillName = match?.skillName ?? s.skillId;
                  return (
                    <li key={s.skillId} className="explore-criteria__chip">
                      <span className="explore-criteria__chip-name">
                        {skillName}
                      </span>
                      <span
                        className="explore-criteria__chip-level"
                        title={`Livello richiesto: ${LIVELLO_LABELS[s.requiredLevel]}`}
                      >
                        {s.requiredLevel.toUpperCase()} ·{' '}
                        {LIVELLO_LABELS[s.requiredLevel]}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {matches.length === 0 ? (
            <EmptyState role="status">
              Nessuna persona o candidato possiede le skill cercate.
            </EmptyState>
          ) : (
            <>
              {/* Due elenchi distinti: prima le persone (utenti reali), poi i
                  candidati esterni. Ciascuno con il proprio titolo; le sezioni
                  senza risultati non vengono mostrate. */}
              <MatchSection
                title="Team"
                matches={people}
                backTo={`/explore/${id}`}
                emptyMessage="Nessuna persona del team possiede le skill cercate."
              />
              <MatchSection
                title="Candidati"
                matches={candidates}
                backTo={`/explore/${id}`}
                emptyMessage="Nessun candidato possiede le skill cercate."
              />
            </>
          )}
        </>
      )}

      {/* Modifica */}
      <Modal
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Modifica esplorazione"
        footer={
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsEditOpen(false)}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              form="explore-edit-form"
              variant="primary"
              loading={updateExploration.isPending}
              disabled={!canSubmit}
            >
              Salva
            </Button>
          </>
        }
      >
        <form
          id="explore-edit-form"
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canSubmit) return;
            setFormError(null);
            updateExploration.mutate({
              name: trimmedName,
              skills: selectedIds.map((sid) => ({
                skillId: sid,
                requiredLevel: requiredLevels[sid]!,
              })),
            });
          }}
        >
          <Field id="explore-edit-name" label="Nome">
            <TextInput
              id="explore-edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>

          <Field
            id="explore-edit-skills"
            label="Skill cercate"
            hint="Aggiungi le skill da cercare e, per ciascuna, il livello minimo richiesto."
          >
            {skillsQuery.isLoading ? (
              <Loading>Caricamento skill…</Loading>
            ) : skillsQuery.isError ? (
              <Alert tone="error">Impossibile caricare il catalogo skill.</Alert>
            ) : (
              <ExplorationSkillEditor
                id="explore-edit-skills"
                options={skillOptions}
                selectedIds={selectedIds}
                requiredLevels={requiredLevels}
                onChangeIds={setSelectedIds}
                onChangeRequiredLevels={setRequiredLevels}
              />
            )}
          </Field>

          {selectedIds.length > 0 && !allLevelsChosen && (
            <Alert tone="info">
              Scegli un livello richiesto per ogni skill selezionata.
            </Alert>
          )}
          {formError !== null && <Alert tone="error">{formError}</Alert>}
        </form>
      </Modal>
    </section>
  );
}
